import {ErrorState, SentinelLoading} from 'presentation/components';
import {translations} from 'presentation/translations-new';
import React, {useMemo} from 'react';
import {ActionTypeLoggingAccessEnum} from 'domain/usecases/private/my-hospitalization/post-logging-access';
import {EmptySearchState} from '../../components/EmptySearchState';
import {LayoutContainer} from '../../components/LayoutContainer';
import {LoadingSkeleton} from '../../components/LoadingSkeleton';
import {ProceduresCardGrid} from './components/ProceduresCardGrid';
import {useProcedures} from './hooks/useProcedures';
import {EmptyStateHospitalization} from '../../components/EmptyState';
import {useMyHospitalizationLoggingAccess} from '../../hooks/useMyHospitalizationLoggingAccess';

const {searchPlaceholder, error, searchEmpty, empty} =
	translations['pt-br'].privateFlow.myHospitalization.procedures;

export const MyHospitalizationProcedures = () => {
	useMyHospitalizationLoggingAccess(ActionTypeLoggingAccessEnum.PROCEDURES);
	const {
		items,
		loadingItemsList,
		isLoading,
		itemsListIsEmpty,
		itemsListHasError,
		loadMore,
		refresh,
		searchValueState,
		debouncedQueryFunc,
		clearSearchParams,
		abortDebounce,
	} = useProcedures();

	const SEARCH_CONFIGS = useMemo(
		() => ({
			searchProps: {
				placeholder: searchPlaceholder,
				clearSearch: clearSearchParams,
				handleChange: () => {
					abortDebounce();
					debouncedQueryFunc();
				},
				value: searchValueState,
			},
		}),
		[searchValueState],
	);

	if (isLoading) {
		return (
			<LayoutContainer filterConfig={SEARCH_CONFIGS}>
				<LoadingSkeleton />
			</LayoutContainer>
		);
	}

	if (itemsListHasError) {
		return (
			<LayoutContainer>
				<ErrorState
					title={error.title}
					description={error.description}
					handleRefresh={refresh}
				/>
			</LayoutContainer>
		);
	}

	if (itemsListIsEmpty && searchValueState.length) {
		return (
			<LayoutContainer filterConfig={SEARCH_CONFIGS}>
				<EmptySearchState
					title={searchEmpty.title}
					description={searchEmpty.description}
					clearFilter={clearSearchParams}
				/>
			</LayoutContainer>
		);
	}

	if (itemsListIsEmpty) {
		return (
			<LayoutContainer>
				<EmptyStateHospitalization
					iconName="clipboard-pulse-outline"
					title={empty.title}
					description={empty.description}
				/>
			</LayoutContainer>
		);
	}

	return (
		<LayoutContainer filterConfig={SEARCH_CONFIGS}>
			<ProceduresCardGrid list={items} />
			<SentinelLoading
				minHeight="200px"
				onVisible={() => {
					if (items?.length > 0) {
						loadMore();
					}
				}}
				isLoading={loadingItemsList}
			/>
		</LayoutContainer>
	);
};
